import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';
import FacilityCard from './components/FacilityCard';
import FacilityDetailsModal from './components/FacilityDetailsModal';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';


const RegionalNetworkDirectory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState('all');
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);

  const profile = { role: 'clinician' };

  const countries = [
    { value: 'all', label: 'All Countries' },
    { value: 'saint-lucia', label: 'Saint Lucia' },
    { value: 'martinique', label: 'Martinique' },
    { value: 'barbados', label: 'Barbados' },
    { value: 'trinidad', label: 'Trinidad' },
    { value: 'miami', label: 'Miami, USA' }
  ];

  const modalities = ['MRI', 'Ultrasound'];

  const insuranceProviders = [
    { value: 'all', label: 'All Insurance Providers' },
    { value: 'sagicor', label: 'Sagicor' },
    { value: 'guardian-group', label: 'Guardian Group' },
    { value: 'nagico', label: 'Nagico' },
    { value: 'cg-united', label: 'CG United' }
  ];

  const facilities = [
    {
      id: 1,
      name: 'La Clinic Du Corps Ltd.',
      country: 'Saint Lucia',
      type: 'Tertiary',
      location: { lat: 14.0939, lng: -60.9562 },
      address: 'Cocoville, Rodney Bay, St. Lucia',
      plusCode: '32CV+9J Rodney Bay, St Lucia',
      phone: '+1 758-451-6559',
      whatsapp: '+1 758-721-9950',
      modalities: ['MRI', 'Ultrasound'],
      operatingHours: '9:00 AM - 5:00 PM',
      waitTime: '1-2 days elective, Immediate emergency',
      insurance: ['Sagicor', 'Guardian Group', 'Nagico', 'CG United'],
      specialties: ['Comprehensive Imaging', 'Cardiac Imaging', 'Neuroradiology', 'Interventional Radiology'],
      emergency: false,
      rating: 4.8,
      accreditation: 'JCI Accredited',
      googleMapsUrl: 'https://maps.app.goo.gl/5tpar4BU8wBH3qSg7'
    }
  ];

  const filteredFacilities = facilities?.filter(facility => {
    const matchesSearch = facility?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         facility?.address?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCountry = selectedCountry === 'all' || facility?.country?.toLowerCase()?.replace(', usa', '')?.replace(' ', '-') === selectedCountry;
    const matchesModalities = selectedModalities?.length === 0 || 
                             selectedModalities?.every(mod => facility?.modalities?.includes(mod));
    const matchesInsurance = selectedInsurance === 'all' || 
                            facility?.insurance?.some(ins => 
                              ins?.toLowerCase()?.replace(/\s+/g, '-') === selectedInsurance
                            );
    const matchesEmergency = !showEmergencyOnly || facility?.emergency;

    return matchesSearch && matchesCountry && matchesModalities && matchesInsurance && matchesEmergency;
  });

  const handleModalityToggle = (modality) => {
    setSelectedModalities(prev =>
      prev?.includes(modality)
        ? prev?.filter(m => m !== modality)
        : [...prev, modality]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation userRole={profile?.role || 'clinician'} />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          {/* Header */}
          <div className="bg-card border-b border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPin" size={24} color="var(--color-accent)" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Regional Network Directory</h1>
                  <p className="text-sm text-muted-foreground">
                    Comprehensive Caribbean imaging facilities and tertiary referral centers
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Google Maps Link Section */}
            <div className="mb-6">
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">La Clinic Du Corps Ltd.</h2>
                      <p className="text-sm text-muted-foreground">Cocoville, Rodney Bay, St. Lucia</p>
                      {facilities?.[0]?.plusCode && (
                        <div className="flex items-center gap-2 mt-2">
                          <Icon name="MapPin" size={16} className="text-accent" />
                          <button
                            onClick={() => window.open(facilities?.[0]?.googleMapsUrl, '_blank')}
                            className="text-sm text-accent hover:underline font-medium"
                          >
                            Open in Google Maps - {facilities?.[0]?.plusCode}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => window.open(`tel:${facilities?.[0]?.phone}`)}
                        className="bg-accent hover:bg-accent/90 text-accent-foreground"
                      >
                        <Icon name="Phone" size={18} />
                        <span className="hidden sm:inline ml-2">Call</span>
                      </Button>
                      <Button
                        onClick={() => window.open(`https://wa.me/${facilities?.[0]?.whatsapp?.replace(/[^0-9]/g, '')}`)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Icon name="MessageCircle" size={18} />
                        <span className="hidden sm:inline ml-2">WhatsApp</span>
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-muted/30 border-t border-border">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} className="text-accent" />
                      <span className="text-muted-foreground">Cocoville, Rodney Bay, St. Lucia</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Phone" size={16} className="text-accent" />
                      <a href={`tel:${facilities?.[0]?.phone}`} className="text-accent hover:underline">
                        {facilities?.[0]?.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} className="text-accent" />
                      <span className="text-muted-foreground">{facilities?.[0]?.operatingHours}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <Input
                  type="text"
                  placeholder="Search facilities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  icon="Search"
                />
                
                <Select
                  options={countries}
                  value={selectedCountry}
                  onChange={setSelectedCountry}
                  placeholder="Select country"
                />

                <Select
                  options={insuranceProviders}
                  value={selectedInsurance}
                  onChange={setSelectedInsurance}
                  placeholder="Select insurance"
                />

                <div className="flex items-center gap-2">
                  <Checkbox
                    label="24/7 Emergency Only"
                    checked={showEmergencyOnly}
                    onChange={(e) => setShowEmergencyOnly(e?.target?.checked)}
                  />
                </div>
              </div>

              {/* Modality Filters */}
              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground mb-3">Filter by Imaging Modality:</p>
                <div className="flex flex-wrap gap-2">
                  {modalities?.map(modality => (
                    <button
                      key={modality}
                      onClick={() => handleModalityToggle(modality)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedModalities?.includes(modality)
                          ? 'bg-accent text-accent-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {modality}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Panel */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="BarChart3" size={20} color="var(--color-accent)" />
                  <h3 className="font-semibold text-foreground">Network Statistics</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Facilities</span>
                    <span className="text-lg font-bold text-foreground">{filteredFacilities?.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">24/7 Emergency</span>
                    <span className="text-lg font-bold text-accent">
                      {filteredFacilities?.filter(f => f?.emergency)?.length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Countries Covered</span>
                    <span className="text-lg font-bold text-foreground">
                      {new Set(filteredFacilities.map(f => f.country))?.size}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Facility Cards */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">
                  {filteredFacilities?.length} Facilities Found
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCountry('all');
                      setSelectedModalities([]);
                      setSelectedInsurance('all');
                      setShowEmergencyOnly(false);
                    }}
                    className="bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredFacilities?.map(facility => (
                  <FacilityCard
                    key={facility?.id}
                    facility={facility}
                    onClick={() => setSelectedFacility(facility)}
                  />
                ))}
              </div>

              {filteredFacilities?.length === 0 && (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No facilities found</h3>
                  <p className="text-muted-foreground mb-4">Try adjusting your filters</p>
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCountry('all');
                      setSelectedModalities([]);
                      setSelectedInsurance('all');
                      setShowEmergencyOnly(false);
                    }}
                  >
                    Reset All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Facility Details Modal */}
      {selectedFacility && (
        <FacilityDetailsModal
          facility={selectedFacility}
          onClose={() => setSelectedFacility(null)}
        />
      )}
    </div>
  );
};

export default RegionalNetworkDirectory;